import { NavLink } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import {
  LayoutDashboard, CheckSquare, Columns3, Tags,
  Users, Package, Settings, LogOut, Menu, X, KeyRound
} from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/tasks', icon: CheckSquare, label: 'All Tasks' },
  { to: '/kanban', icon: Columns3, label: 'Team Task Board' },
  { to: '/categories', icon: Tags, label: 'Categories' },
  { to: '/team', icon: Users, label: 'Team' },
  { to: '/packages', icon: Package, label: 'Packages' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);
  const isAdmin = user?.role === 'admin';

  const handleChangePassword = async () => {
    if (!newPw.trim()) { toast.error('New password is required'); return; }
    setChangePwLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPw, new_password: newPw.trim() });
      toast.success('Password changed successfully');
      setShowChangePw(false); setCurrentPw(''); setNewPw('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setChangePwLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-[#34495e] text-white">
        <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
          <Menu size={24} />
        </button>
        <span className="font-black text-lg tracking-tight">Task Manager</span>
        <div className="w-6" />
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-64 bg-[#34495e] text-white flex flex-col z-50
          transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
        data-testid="sidebar"
      >
        <div className="p-6 flex items-center justify-between">
          <h1 className="text-xl font-black tracking-tight">Task Manager</h1>
          <button className="md:hidden" onClick={() => setSidebarOpen(false)} data-testid="close-sidebar-btn">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {navItems.map(item => {
            if (item.to === '/settings' && !isAdmin) return null;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm
                  transition-colors duration-150
                  ${isActive
                    ? 'bg-white/15 text-white font-bold'
                    : 'text-white/70 hover:bg-white/10 hover:text-white font-normal'}
                `}
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-sm bg-white/20 flex items-center justify-center text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => { setShowChangePw(true); setSidebarOpen(false); }}
            data-testid="change-password-btn"
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors w-full px-2 py-1.5"
          >
            <KeyRound size={16} />
            Change password
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors w-full px-2 py-1.5"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="md:ml-64 min-h-screen p-6 md:p-8">
        {children}
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showChangePw} onOpenChange={(open) => { if (!open) { setShowChangePw(false); setCurrentPw(''); setNewPw(''); } }}>
        <DialogContent className="max-w-sm bg-white border border-[#E2E8F0] rounded-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">Change Password</DialogTitle>
            <DialogDescription className="text-sm text-[#7F8C8D]">Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Current Password</Label>
              <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} placeholder="Enter current password" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">New Password</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Enter new password" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="new-password-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }} />
            </div>
            <Button onClick={handleChangePassword} disabled={changePwLoading} className="w-full bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="change-password-submit">
              {changePwLoading ? 'Updating...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
